import React, { useEffect } from 'react';
import EmptyLayout from '@/components/Layout/EmptyLayout';
import { Redirect } from 'react-router-dom';
import { useLocation, useRouteMatch, useHistory } from 'react-router';
import {
  CompanyNotFoundError,
  useFilterLoading,
  useOptionalFilterState,
  useSetFilterFromSlug,
  useSetUrlById,
} from '@/hooks/useFilter';
import {
  BusinessUnitNotFoundError,
  ProjectNotFoundError,
  useProjectsLoading,
} from '@/hooks/useProjectOptions';
import { LoadingMessageCard } from '@/components/LoadingMessageCard';
import { ContextOutOfReachError } from '@/hooks/contextReach';
import { toast } from 'sonner';

/**
 * Why the URL could not be turned into a context. `not-found` is a slug none
 * of the user's roles know about (or a project out of their reach): the user
 * is told about the missing permission and the chooser stays put.
 * `out-of-reach` is a company or business unit the user does have roles in,
 * only not at that level (BIL-5009): the chooser is where they pick one of
 * their own contexts, and it may forward them right away when there is a
 * single one.
 */
type Bounce = { kind: 'not-found' } | { kind: 'out-of-reach' };

export const SelectCompany: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const match = useRouteMatch<{
    company?: string;
    businessUnit?: string;
    project?: string;
  }>();
  const { search } = useLocation();
  const history = useHistory();
  const ids = React.useMemo(() => {
    const query = new URLSearchParams(search);
    return query.get('company_id')
      ? {
          company_id: Number(query.get('company_id')),
          project_id: query.get('project_id')
            ? Number(query.get('project_id'))
            : undefined,
        }
      : undefined;
  }, [search]);
  const filter = useOptionalFilterState();
  const setFilterFromSlug = useSetFilterFromSlug();
  const setUrlFromId = useSetUrlById();
  const filterLoading = useFilterLoading();
  const projectsLoading = useProjectsLoading();

  const [bounce, setBounce] = React.useState<Bounce | null>(null);

  useEffect(() => {
    if (filterLoading || projectsLoading) return;
    if (bounce) {
      if (bounce.kind === 'out-of-reach') {
        toast.info('Elegí un proyecto para continuar.', { duration: 4000 });
      } else if (match.params.company) {
        // we tried to access a company (from the url), but we couldn't. We probably didnt have permissions
        toast.error(
          'Estas tratando de acceder a una compañia o proyecto para la que no tenes permisos. Contactate con tu manager para que te conceda los permisos necesarios.',
          { duration: 5000 }
        );
      }
      return;
    }
    try {
      if (ids) {
        setUrlFromId(ids);
      } else if (match.params.businessUnit && match.params.project) {
        // two middle segments in the URL: businessUnit + project
        setFilterFromSlug({
          companySlug: match.params.company!,
          businessUnitSlug: match.params.businessUnit,
          projectSlug: match.params.project,
        });
      } else if (match.params.businessUnit) {
        // a single middle segment is always a business unit slug. Legacy
        // /:company/:project deep links are not supported: an unknown slug
        // goes through the not-found path below (/choose_company redirect).
        setFilterFromSlug({
          companySlug: match.params.company!,
          businessUnitSlug: match.params.businessUnit,
        });
      } else {
        setFilterFromSlug({
          companySlug: match.params.company!,
        });
      }
    } catch (e) {
      if (e instanceof ContextOutOfReachError && e.level !== 'project') {
        setBounce({ kind: 'out-of-reach' });
      } else if (
        e instanceof CompanyNotFoundError ||
        e instanceof ProjectNotFoundError ||
        e instanceof BusinessUnitNotFoundError ||
        e instanceof ContextOutOfReachError
      ) {
        setBounce({ kind: 'not-found' });
      } else {
        throw e;
      }
    }
  }, [
    match.params.company,
    match.params.businessUnit,
    match.params.project,
    setFilterFromSlug,
    bounce,
    ids,
    setUrlFromId,
    filterLoading,
    projectsLoading,
  ]);

  if (bounce) {
    // the page the user was heading to, query string included, minus the
    // id params this component consumes itself (they would resolve again on
    // the way back and bounce here once more)
    const destinationQuery = new URLSearchParams(search);
    destinationQuery.delete('company_id');
    destinationQuery.delete('project_id');
    const destinationSearch = destinationQuery.toString();
    return (
      <Redirect
        to={{
          pathname: '/choose_company',
          // stay in the choose company page if we had a company slug but it didn't work
          // if we were just trying to go to somewhere, let /choose_company redirect us after choosing if it can
          // A context out of reach is different: the user does have roles in
          // that company, so /choose_company may land them straight on their
          // only context, keeping the page they were heading to.
          state: {
            stay: bounce.kind !== 'out-of-reach' && !!match.params.company,
            nextDestination:
              match.url + (destinationSearch ? `?${destinationSearch}` : ''),
          },
        }}
      />
    );
  } else {
    // Send the user to his destination if filter is ready
    if (
      !filterLoading &&
      !projectsLoading &&
      filter.kind === 'selected' &&
      !!match.params.company === !!filter.value.company_id
    ) {
      if (ids) {
        const query = new URLSearchParams(search);
        query.delete('company_id');
        query.delete('project_id');
        history.replace({ search: query.toString() });
      }
      return <>{children}</>;
    } else {
      return (
        <EmptyLayout>
          <div className="w-4/5 md:w-1/3 min-w-80 max-w-lg m-auto">
            <LoadingMessageCard message="Validando permisos" />
          </div>
        </EmptyLayout>
      );
    }
  }
};
